/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {Calendar} from "./Calendar";
import {COLORS} from "@res/colors";
import {OKButton} from "../../widget/OKButton";
import {
	padding_rl_lg,
	padding_tb_lg
} from "@styles/styles";
import {FONTS} from "@res/fonts";
import {HGap} from "../../widget/HGap";
import dayjs from "../../../utils/dayjs";

type Props = {
	onFilter: (startDate: TimeAndPretty, endDate: TimeAndPretty) => void
	onClear: () => void,
	filterDate?: {
		start: TimeAndPretty
		end: TimeAndPretty
	},
	minDate?:Date
}

export type TimeAndPretty = {
	name: string,
	timestamp: number
}

type State = {
	startDate?: TimeAndPretty,
	endDate?: TimeAndPretty,
	selected: 'startDate' | 'endDate'
}

export class CalendarDialog
	extends React.Component<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = {
			selected: "startDate"
		};
	}

	formatDateNumbers = (date: number) => {
		if (date.toString().length === 1)
			return `0${date}`;
		return date.toString();
	};


	inputStyle = (green: boolean, bold: boolean) => {
		return {
			width: '110px', height: '32px', border: 'solid',
			borderWidth: green ? '2px' : '1px',
			margin: green ? '0px' : '1px', // to account for border width change (don't use border-box, it breaks internal margin)
			borderColor: green ? '#0ac5b7' : COLORS.grays_2
		};
	};

	render() {
		return <div
			className={`ll_v_s ${padding_tb_lg} ${padding_rl_lg}`}
			style={{
				width: '400px',
				maxWidth: '400px',
				boxSizing: 'border-box',
				boxShadow: "0px 2px 5px 0 rgba(0, 0, 0, 0.28)",
				backgroundColor: COLORS.white
			}}>
			{this.renderHead()}
			{this.renderCalendar()}
			{this.renderButtons()}
		</div>;
	}

	private renderHead = () => {
		const startDateColor = this.state.startDate !== undefined ? COLORS.grays_dark : COLORS.grays_2;
		const endDateColor = this.state.endDate !== undefined ? COLORS.grays_dark : COLORS.grays_2;
		return <div className={'ll_h_c'} style={{flex: 1, justifyContent: 'space-between'}}>
			<div className={'ll_h_c'}>
				{FONTS.reg_16('from')}
				<HGap h={10}/>
				<div
					className={'ll_h_c'}
					style={this.inputStyle(this.state.selected === 'startDate', this.state.startDate !== undefined)}
					onClick={() => this.setState({selected: 'startDate'})}
				>
					{FONTS.reg_16(this.state.startDate?.name || this.props.filterDate?.start.name || 'mm/dd/yyyy', {marginLeft: 5, color: startDateColor})}
				</div>
			</div>
			<div className={'ll_h_c'}>
				{FONTS.reg_16('to')}
				<HGap h={10}/>
				<div
					className={'ll_h_c'}
					style={this.inputStyle(this.state.selected === 'endDate', this.state.endDate !== undefined)}
					onClick={() => this.setState({selected: 'endDate'})}
				>
					{FONTS.reg_16(this.state.endDate?.name || this.props.filterDate?.end.name || 'mm/dd/yyyy', {marginLeft: 5, color: endDateColor})}
				</div>
			</div>
		</div>;
	};

	private renderButtons = () => <div className={'ll_h_c'} style={{flex: 1, justifyContent: 'space-between', backgroundColor: COLORS.white}}>
		<div className={'clickable'} onClick={() => {
			this.setState({startDate: undefined, endDate: undefined});
			this.props.onClear();
		}}>
			{FONTS.reg_16('Clear Filter')}
		</div>
		<OKButton
			text={"Apply"}
			enabled={!!this.state.startDate && !!this.state.endDate}
			onClick={() => this.props.onFilter(this.state.startDate!, this.state.endDate!)}/>
	</div>;

	private renderCalendar = () => {
		const onFilter = (date: Date) => {
			const stateDate = dayjs(date).toDate();
			if (this.state.selected === 'endDate') {
				// In case the user is selecting the end date, include the whole day up to last ms.
				stateDate.setHours(23, 59, 59, 999);
			}

			return this.setState(state => ({
				selected: state.selected === 'startDate' ? 'endDate' : state.selected,
				[state.selected]: {
					name: `${this.formatDateNumbers(date.getMonth() + 1)}/${this.formatDateNumbers(stateDate.getDate())}/${stateDate.getFullYear().toString()}`,
					timestamp: stateDate.getTime()
				}
			}));
		};
		return <Calendar onFilter={onFilter} minDate={this.props.minDate}/>;
	};
}
